import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { extractCurrentWord, matchTriggers } from "@/lib/trigger-matcher.ts";
import { TRIGGER_WORDS } from "@/lib/trigger-words.ts";
import {
  createInitialState,
  shouldSkipStep,
  typeaheadReducer,
} from "@/lib/typeahead-state-machine.ts";
import {
  type ContactEntry,
  type ExternalInputs,
  type FlowStep,
  type FlowType,
  GUIDED_STEPS,
  type LocationEntry,
  type MatchResult,
  type TextSegment,
  type TypeaheadState,
} from "./types.ts";

/** Ref API exposed by the guided flow, matching the existing dropdown pattern. */
interface GuidedFlowRef {
  handleArrowUp: () => void;
  handleArrowDown: () => void;
  /** Accept the highlighted item. Returns `true` if something was selected. */
  handleEnter: () => boolean;
  /** Tab accepts highlighted item when present, otherwise skips the step. */
  handleTab: () => boolean;
  /** Shift+Tab navigates back to the previous guided step. */
  handleShiftTab: () => boolean;
}

/** Per-trigger quick-pick suggestions for the context step. */
export const CONTEXT_SUGGESTIONS: Partial<Record<string, string[]>> = {
  Arrange: ["a meeting", "a call", "a get-together", "lunch"],
  Block: ["focus time", "a meeting slot", "personal time", "deep work"],
  Book: ["a meeting", "a haircut", "a dentist appointment", "a restaurant"],
  Breakfast: ["a breakfast meeting", "a working breakfast"],
  "Catch up": ["over coffee", "over lunch", "over drinks"],
  "Check in": ["a quick check-in", "a 1:1", "a standup"],
  Coffee: ["a coffee chat", "a coffee date"],
  Connect: ["over coffee", "for a call", "over lunch", "for a chat"],
  Coordinate: ["schedules", "a meeting time", "calendars"],
  Dinner: ["a dinner date", "a dinner reservation", "a team dinner"],
  Drinks: ["after work", "a happy hour", "a cocktail hour"],
  "Find time": ["for a meeting", "for a call", "for a catch-up", "for lunch"],
  "Get together": ["for lunch", "for drinks", "for dinner"],
  Grab: ["coffee", "lunch", "drinks", "a bite"],
  Hold: ["a meeting slot", "focus time", "a block", "an hour"],
  Invite: ["to a meeting", "to lunch", "to a call", "to coffee"],
  Lunch: ["a lunch meeting", "a working lunch"],
  Make: [
    "a restaurant reservation",
    "a dentist appointment",
    "a nail appointment",
    "a hair appointment",
  ],
  Organize: ["a meeting", "a party", "an event", "a get-together"],
  Plan: ["a meeting", "a session", "an event", "a get-together"],
  Reschedule: ["a meeting", "a call", "a 1:1", "an appointment"],
  Schedule: ["a meeting", "a call", "a 1:1", "a sync"],
  "Set up": ["a meeting", "a call", "a 1:1", "a sync"],
  Sync: ["on a project", "on progress", "on goals", "a quick sync"],
};

/** Context strings that indicate an appointment flow (single-select serviceProvider). */
const APPOINTMENT_CONTEXTS = new Set([
  "a haircut",
  "a dentist appointment",
  "a doctor appointment",
  "a massage",
  "a manicure",
  "a physical therapy session",
  "a spa appointment",
  "a vet appointment",
  "an oil change",
  "a car wash",
  "a nail appointment",
  "a hair appointment",
]);

/** Context strings that indicate a reservation flow (single-select venue + multi-select contacts). */
const RESERVATION_CONTEXTS = new Set(["a restaurant", "a restaurant reservation", "a reservation"]);

/** Context strings that indicate the connector flow (single-select service). */
const CONNECTOR_CONTEXTS = new Set(["a connector"]);

/** Derive flow type from a context string. */
function flowTypeForContext(context: string): FlowType {
  if (RESERVATION_CONTEXTS.has(context)) return "reservation";
  if (APPOINTMENT_CONTEXTS.has(context)) return "appointment";
  if (CONNECTOR_CONTEXTS.has(context)) return "connector";
  return "meeting";
}

interface UseGuidedFlowOptions {
  /** Called on each state change during the guided flow to update the textarea with the progressive sentence. */
  onTextUpdate?: (composedText: string) => void;
  /**
   * Scheduling-flow fields already answered out-of-band at trigger-selection
   * time (e.g. via an active omnibox enrichment). `true` values cause the
   * reducer to skip their matching steps. The hook is agnostic to which
   * enrichment supplied the answer — see {@link OmniboxEnrichment.provides}.
   */
  externalInputs?: ExternalInputs;
}

export interface UseGuidedFlowReturn {
  state: TypeaheadState;
  /** Current trigger-word match results (empty when not in matching step). */
  matchResults: MatchResult[];
  /** Index of the currently highlighted item in the dropdown list. */
  selectedIndex: number;
  /** Whether the typeahead dropdown is visible. */
  isActive: boolean;
  /** Info about the word being matched (for positioning / text replacement). */
  matchContext: { word: string; startIndex: number } | null;
  /** Structured text segments for the overlay (segments with `step` are bold/clickable). */
  segments: TextSegment[];

  // Actions
  /** Call on every input change to detect trigger words. */
  handleInputChange: (text: string, cursorPosition: number) => void;
  selectTrigger: (triggerWord: string) => void;
  setContext: (context: string) => void;
  setConnectorService: (connectorService: string) => void;
  setVenue: (venue: ContactEntry) => void;
  setServiceProvider: (serviceProvider: ContactEntry) => void;
  setPartySize: (partySize: string) => void;
  addContact: (contact: ContactEntry) => void;
  removeContact: (index: number) => void;
  setTime: (timeDate: string) => void;
  setTimeWithRanges: (
    timeDate: string,
    ranges: Array<{ startISO: string; endISO: string }>,
    skipCalendarConflicts?: boolean,
  ) => void;
  setDuration: (duration: string) => void;
  setLocation: (location: LocationEntry) => void;
  setTitle: (title: string) => void;
  /** Skip the current step without setting data. */
  skip: () => void;
  /** Dismiss the typeahead and return to idle. */
  reset: () => void;
  /** Soft-dismiss: preserve data (return to "sent") if any data was filled in. */
  dismiss: () => void;
  /** Pre-fill the scheduling flow from a canned prompt string (e.g. "Coordinate a lunch"). */
  prefill: (text: string) => void;
  setSelectedIndex: (index: number) => void;
  /** Jump to a specific step in the guided flow (used by bold overlay click). */
  jumpToStep: (step: FlowStep) => void;
  /** Preview the connector word for the time step (called by picker on arrow-key navigation). */
  setPreviewTime: (timeDate: string | null) => void;
  /** Preview the connector word for the location step (called by picker on arrow-key navigation). */
  setPreviewLocation: (location: LocationEntry | null) => void;

  /** Imperative ref API for keyboard delegation from SmartTextarea. */
  ref: React.RefObject<GuidedFlowRef | null>;
}

/**
 * Main hook for the guided scheduling flow.
 *
 * Detects scheduling-intent trigger words as the user types, manages the
 * multi-step guided flow state, and composes a structured scheduling request
 * when the user completes the flow.
 *
 * Four flow types: meetings (with people), appointments (at a service
 * provider), reservations (at a venue with people), and connector setup
 * (seeded via a canned phrase rather than trigger-word matching).
 *
 * Follows the same integration pattern as `ContactAutocompleteDropdown` and
 * `SlashCommandDropdown` — exposes an imperative ref API so `SmartTextarea`
 * can delegate keyboard events.
 */
const DEFAULT_EXTERNAL_INPUTS: ExternalInputs = {
  whenProvided: false,
  durationProvided: false,
};

export function useGuidedFlow({
  onTextUpdate,
  externalInputs = DEFAULT_EXTERNAL_INPUTS,
}: UseGuidedFlowOptions = {}): UseGuidedFlowReturn {
  // Read via ref inside selectTrigger so we always see the latest value at
  // trigger-selection time without forcing selectTrigger to re-create on every
  // enrichment change.
  const externalInputsRef = useRef(externalInputs);
  externalInputsRef.current = externalInputs;
  const [state, dispatch] = useReducer(typeaheadReducer, undefined, createInitialState);
  const [matchResults, setMatchResults] = useState<MatchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [matchContext, setMatchContext] = useState<{ word: string; startIndex: number } | null>(
    null,
  );
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Refs that always reflect the current state — used inside debounce callbacks
  // and `dismiss` to avoid stale closures and unnecessary callback re-creation.
  const stepRef = useRef(state.step);
  stepRef.current = state.step;
  const stateRef = useRef(state);
  stateRef.current = state;

  // Preview state — ephemeral hints from pickers used to update connector words
  // in real-time as the user arrows through options (before committing a selection).
  const [previewTime, setPreviewTime] = useState<string | null>(null);
  const [previewLocation, setPreviewLocation] = useState<LocationEntry | null>(null);
  const imperativeRef = useRef<GuidedFlowRef | null>(null);

  // Reset selected index when match results change
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on list length changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [matchResults.length]);

  // Clear any pending debounce when the step advances past idle/matching.
  // This prevents a stale timer from re-dispatching SET_STEP after a trigger
  // has already been selected and the flow has moved on.
  useEffect(() => {
    if (state.step !== "idle" && state.step !== "matching") {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    }
  }, [state.step]);

  // --- Input change: detect trigger words with 20ms debounce ---
  const handleInputChange = useCallback((text: string, cursorPosition: number) => {
    // Use ref for current step — avoids recreating this callback on every step
    // change and prevents stale closures in the debounce timer.
    if (stepRef.current !== "idle" && stepRef.current !== "matching") return;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      const currentStep = stepRef.current;
      if (currentStep !== "idle" && currentStep !== "matching") return;

      const { word, startIndex } = extractCurrentWord(text, cursorPosition);

      // Only initiate the flow when the trigger word is at the very start of the input.
      // Typing a trigger word mid-sentence (e.g. "hey let's schedule") should NOT activate it.
      if (startIndex !== 0) {
        setMatchResults([]);
        setMatchContext(null);
        if (currentStep === "matching") {
          dispatch({ type: "SET_STEP", step: "idle" });
        }
        return;
      }

      const results = matchTriggers(word, TRIGGER_WORDS);

      if (results.length > 0) {
        setMatchResults(results);
        setMatchContext({ word, startIndex });
        if (currentStep === "idle") {
          dispatch({ type: "SET_STEP", step: "matching" });
        }
      } else {
        setMatchResults([]);
        setMatchContext(null);
        if (currentStep === "matching") {
          dispatch({ type: "SET_STEP", step: "idle" });
        }
      }
    }, 20);
  }, []);

  // Clean up debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Clear preview when the step changes (advancing, jumping, or resetting).
  // biome-ignore lint/correctness/useExhaustiveDependencies: only reset on step change
  useEffect(() => {
    setPreviewTime(null);
    setPreviewLocation(null);
  }, [state.step]);

  // Structured text segments — recomputed on every state change during the guided flow.
  // Persists into "sent" step so the bold overlay stays visible after dialogs close.
  const segments = useMemo(() => {
    if (state.step === "idle" || state.step === "matching") return [];
    return composeProgressiveSegments(state, { previewTime, previewLocation });
  }, [state, previewTime, previewLocation]);

  // --- Progressive text update: push plain text to the textarea ---
  // useLayoutEffect fires synchronously before paint so the textarea value is
  // up-to-date before the next frame — prevents visible flash of stale text.
  // biome-ignore lint/correctness/useExhaustiveDependencies: onTextUpdate is stable via useCallback
  useLayoutEffect(() => {
    if (!onTextUpdate) return;
    if (segments.length === 0) return;
    onTextUpdate(segmentsToText(segments));
  }, [segments]);

  // --- Actions ---

  /** Determines the first guided step to show after trigger selection, skipping steps whose data is already filled. */
  const firstUnfilledStep = useCallback(
    (
      trigger: string,
      context?: string,
      time?: string,
      external: ExternalInputs = DEFAULT_EXTERNAL_INPUTS,
    ): FlowStep => {
      // "Meet" reads naturally without context ("Meet with Jamie"); skip the context step.
      const skipsContext = trigger === "Meet" || Boolean(context);
      // Time is satisfied by an explicit time string OR by an enrichment that
      // declared `provides.when`. Duration only skips via `provides.duration`.
      const timeSatisfied = Boolean(time) || external.whenProvided;
      const durationSatisfied = external.durationProvided;
      const flowType: FlowType = context ? flowTypeForContext(context) : "meeting";
      for (const step of GUIDED_STEPS) {
        if (step === "context" && skipsContext) continue;
        if (step === "time" && timeSatisfied) continue;
        if (step === "duration" && durationSatisfied) continue;
        if (shouldSkipStep(flowType, step)) continue;
        return step;
      }
      return GUIDED_STEPS[GUIDED_STEPS.length - 1];
    },
    [],
  );

  const selectTrigger = useCallback(
    (triggerWord: string) => {
      dispatch({
        type: "SELECT_TRIGGER",
        triggerWord,
        externalInputs: externalInputsRef.current,
      });
      setMatchResults([]);
      setMatchContext(null);
      dispatch({
        type: "SET_STEP",
        step: firstUnfilledStep(triggerWord, undefined, undefined, externalInputsRef.current),
      });
    },
    [firstUnfilledStep],
  );

  const setContext = useCallback((context: string) => {
    const flowType = flowTypeForContext(context);
    dispatch({ type: "SET_CONTEXT", context, flowType });
  }, []);

  const setConnectorService = useCallback((connectorService: string) => {
    dispatch({ type: "SET_CONNECTOR_SERVICE", connectorService });
  }, []);

  const setVenue = useCallback((venue: ContactEntry) => {
    dispatch({ type: "SET_VENUE", venue });
  }, []);

  const setServiceProvider = useCallback((serviceProvider: ContactEntry) => {
    dispatch({ type: "SET_SERVICE_PROVIDER", serviceProvider });
  }, []);

  const setPartySize = useCallback((partySize: string) => {
    dispatch({ type: "SET_PARTY_SIZE", partySize });
  }, []);

  const addContact = useCallback((contact: ContactEntry) => {
    dispatch({ type: "ADD_CONTACT", contact });
  }, []);

  const removeContact = useCallback((index: number) => {
    dispatch({ type: "REMOVE_CONTACT", contactIndex: index });
  }, []);

  const setTime = useCallback((timeDate: string) => {
    dispatch({ type: "SET_TIME", timeDate });
  }, []);

  const setTimeWithRanges = useCallback(
    (
      timeDate: string,
      ranges: Array<{ startISO: string; endISO: string }>,
      skipCalendarConflicts?: boolean,
    ) => {
      dispatch({ type: "SET_TIME", timeDate, userConstraintRanges: ranges, skipCalendarConflicts });
    },
    [],
  );

  const setDuration = useCallback((duration: string) => {
    dispatch({ type: "SET_DURATION", duration });
  }, []);

  const setLocation = useCallback((location: LocationEntry) => {
    dispatch({ type: "SET_LOCATION", location });
  }, []);

  const setTitle = useCallback((title: string) => {
    dispatch({ type: "SET_TITLE", title });
  }, []);

  const skip = useCallback(() => {
    dispatch({ type: "SKIP_STEP" });
  }, []);

  const jumpToStep = useCallback((step: FlowStep) => {
    dispatch({ type: "SET_STEP", step });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: "RESET" });
    setMatchResults([]);
    setMatchContext(null);
  }, []);

  // Pre-fill the scheduling flow from a canned prompt (e.g. "Coordinate a lunch").
  // Parses the text to extract trigger, context, and time, then jumps to the first unfilled step.
  const prefill = useCallback(
    (text: string) => {
      const parsed = parseGuidedPrefill(text);
      if (!parsed) return;

      dispatch({
        type: "SELECT_TRIGGER",
        triggerWord: parsed.trigger,
        externalInputs: externalInputsRef.current,
      });
      setMatchResults([]);
      setMatchContext(null);

      if (parsed.context) {
        const flowType = flowTypeForContext(parsed.context);
        dispatch({ type: "SET_CONTEXT", context: parsed.context, flowType });
      }
      if (parsed.time) dispatch({ type: "SET_TIME", timeDate: parsed.time });

      dispatch({
        type: "SET_STEP",
        step: firstUnfilledStep(
          parsed.trigger,
          parsed.context,
          parsed.time,
          externalInputsRef.current,
        ),
      });
    },
    [firstUnfilledStep],
  );

  // Soft-dismiss: return to "sent" if the user has already filled in data,
  // so the bold overlay is preserved. Full reset only when no data exists.
  // Used for click-outside and Escape when a dialog is open.
  // Reads from stateRef so the callback identity is stable (no state deps).
  const dismiss = useCallback(() => {
    const s = stateRef.current;
    const hasData =
      s.triggerWord !== null ||
      s.context !== null ||
      s.venue !== null ||
      s.serviceProvider !== null ||
      s.partySize !== null ||
      s.contacts.length > 0 ||
      s.timeDate !== null ||
      s.duration !== null ||
      s.location !== null ||
      s.title !== null ||
      s.connectorService !== null;
    if (hasData) {
      dispatch({ type: "SET_STEP", step: "sent" });
    } else {
      dispatch({ type: "RESET" });
      setMatchResults([]);
      setMatchContext(null);
    }
  }, []);

  // --- Imperative ref for keyboard delegation ---

  const handleArrowUp = useCallback(() => {
    if (state.step === "matching" && matchResults.length > 0) {
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    }
  }, [state.step, matchResults.length]);

  const handleArrowDown = useCallback(() => {
    if (state.step === "matching" && matchResults.length > 0) {
      setSelectedIndex((prev) => Math.min(prev + 1, matchResults.length - 1));
    }
  }, [state.step, matchResults.length]);

  const handleEnter = useCallback((): boolean => {
    if (state.step === "matching" && matchResults.length > 0) {
      const selected = matchResults[selectedIndex];
      if (selected) {
        selectTrigger(selected.word);
        return true;
      }
    }
    // During contacts/venue/serviceProvider step, Enter from the textarea confirms and advances
    if (state.step === "contacts" || state.step === "venue" || state.step === "serviceProvider") {
      skip();
      return true;
    }
    return false;
  }, [state.step, matchResults, selectedIndex, selectTrigger, skip]);

  const handleTab = useCallback((): boolean => {
    // During matching: Tab accepts the highlighted item (same as Enter)
    if (state.step === "matching" && matchResults.length > 0) {
      const selected = matchResults[selectedIndex];
      if (selected) {
        selectTrigger(selected.word);
        return true;
      }
    }
    // During guided flow steps: Tab skips the current step
    if (GUIDED_STEPS.includes(state.step)) {
      skip();
      return true;
    }
    return false;
  }, [state.step, matchResults, selectedIndex, selectTrigger, skip]);

  const handleShiftTab = useCallback((): boolean => {
    const stepIdx = GUIDED_STEPS.indexOf(state.step);
    if (stepIdx <= 0) return false;
    // Walk backwards, skipping steps irrelevant for the current flow type
    for (let i = stepIdx - 1; i >= 0; i--) {
      if (!shouldSkipStep(state.flowType, GUIDED_STEPS[i])) {
        jumpToStep(GUIDED_STEPS[i]);
        return true;
      }
    }
    return false;
  }, [state.step, state.flowType, jumpToStep]);

  // Keep the ref up to date
  imperativeRef.current = {
    handleArrowUp,
    handleArrowDown,
    handleEnter,
    handleTab,
    handleShiftTab,
  };

  const isActive = useMemo(() => state.step !== "idle" && state.step !== "sent", [state.step]);

  // Memoize the return object so consumers (e.g. SmartTextarea's useCallback
  // deps) don't see a new object identity on every render. The object only
  // changes when data values change — all action callbacks are already stable.
  // biome-ignore lint/correctness/useExhaustiveDependencies: callbacks are stable, only data values matter
  return useMemo(
    () => ({
      state,
      matchResults,
      selectedIndex,
      isActive,
      matchContext,
      segments,
      handleInputChange,
      selectTrigger,
      setContext,
      setConnectorService,
      setVenue,
      setServiceProvider,
      setPartySize,
      addContact,
      removeContact,
      setTime,
      setTimeWithRanges,
      setDuration,
      setLocation,
      setTitle,
      skip,
      reset,
      dismiss,
      prefill,
      setSelectedIndex,
      jumpToStep,
      setPreviewTime,
      setPreviewLocation,
      ref: imperativeRef,
    }),
    [state, matchResults, selectedIndex, isActive, matchContext, segments, handleInputChange],
  );
}

/**
 * Days of the week that require the "on" preposition.
 * e.g. "Meet with Jamie on Monday" but "Meet with Jamie tomorrow" (no "on").
 */
const DAYS_OF_WEEK = new Set([
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
]);

/**
 * Trigger words that take "a meeting" as an object.
 * e.g. "Schedule a meeting with…", "Book a meeting with…"
 *
 * Other triggers read naturally without it:
 * "Meet with…", "Catch up with…", "Sync with…"
 */
const MEETING_OBJECT_TRIGGERS = new Set([
  "Arrange",
  "Block",
  "Book",
  "Coordinate",
  "Hold",
  "Organize",
  "Plan",
  "Reschedule",
  "Schedule",
  "Set up",
]);

/**
 * Compose structured text segments from the current state.
 *
 * Each segment is either plain connector text or a clickable detail filled
 * via a guided-flow step. The overlay renders segments with `step` as bold
 * and clickable; the plain text is `segments.map(s => s.text).join("")`.
 *
 *   "Schedule a meeting with **Jamie (jamie@ex.com)** on **Tomorrow** for **1 hour**, **virtually**"
 *   "Meet with **Alex** on **Next week** for **30 min** at **123 Main St**"
 */
interface PreviewHints {
  previewTime?: string | null;
  previewLocation?: LocationEntry | null;
}

/** Exported for tests — production callers stay inside this hook. */
export function composeProgressiveSegments(
  state: TypeaheadState,
  preview?: PreviewHints,
): TextSegment[] {
  // Connector flow composes its own fixed sentence ("Connect my Notion") —
  // the backend's connector word matches this phrasing, and none of the
  // scheduling segments below apply. The trigger word is deliberately ignored.
  if (state.flowType === "connector") {
    const connectorSegments: TextSegment[] = [{ text: "Connect my " }];
    if (state.connectorService) {
      connectorSegments.push({ text: state.connectorService.trim(), step: "connectorService" });
    }
    return connectorSegments;
  }

  const segments: TextSegment[] = [];

  const trigger = (state.triggerWord ?? "Schedule").trim();
  segments.push({ text: trigger });

  // Context replaces the generic "a meeting" for triggers that went through the context step.
  // For non-context triggers that need an object noun, fall back to "a meeting".
  const hasAnyDetail =
    state.venue !== null ||
    state.serviceProvider !== null ||
    state.partySize !== null ||
    state.contacts.length > 0 ||
    state.timeDate !== null ||
    state.duration !== null ||
    state.location !== null;

  if (state.context) {
    segments.push({ text: " " });
    segments.push({ text: state.context.trim(), step: "context" });
  } else if (hasAnyDetail && MEETING_OBJECT_TRIGGERS.has(trigger)) {
    segments.push({ text: " a meeting" });
  }

  // Venue (reservation) — "at Olive Garden"
  if (state.venue) {
    segments.push({ text: " at " });
    segments.push({ text: state.venue.name.trim(), step: "venue" });
  }

  // Service provider (appointment) — "at Supercuts"
  if (state.serviceProvider) {
    segments.push({ text: " at " });
    segments.push({ text: state.serviceProvider.name.trim(), step: "serviceProvider" });
  }

  // Party size (reservation) — "for 4 people"
  if (state.partySize) {
    segments.push({ text: " for " });
    segments.push({ text: state.partySize.trim(), step: "partySize" });
  }

  // Contacts — always uses "with" connector
  if (state.contacts.length > 0) {
    segments.push({ text: " with " });
    segments.push({ text: formatContacts(state.contacts), step: "contacts" });
  }

  // Time → "on Monday" (preposition only for day-of-week) or "tomorrow" / "next week"
  if (state.timeDate) {
    const time = state.timeDate.trim();
    const timeLower = time.toLowerCase();
    const isDayOfWeek =
      DAYS_OF_WEEK.has(timeLower) ||
      Array.from(DAYS_OF_WEEK).some((day) => timeLower.startsWith(day));
    const hasCustomRanges = state.userConstraintRanges && state.userConstraintRanges.length > 0;
    segments.push({ text: isDayOfWeek ? " on " : " " });
    segments.push({ text: isDayOfWeek || hasCustomRanges ? time : timeLower, step: "time" });
  }

  // Duration → "for 1 hour"
  if (state.duration) {
    segments.push({ text: " for " });
    segments.push({ text: state.duration.trim(), step: "duration" });
  }

  // Location → ", virtually" / "at 123 Main St" / " at " (in-person, address pending)
  if (state.location) {
    if (state.location.type === "virtual") {
      segments.push({ text: ", " });
      segments.push({ text: "virtually", step: "location" });
    } else if (state.location.details) {
      segments.push({ text: " at " });
      segments.push({ text: state.location.details.trim(), step: "location" });
    } else if (state.step === "location") {
      // In-person chosen but address not entered yet — show "at" connector preview
      segments.push({ text: " at " });
    }
  }

  // Title → ", titled "Q1 sync""
  if (state.title) {
    segments.push({ text: ', titled "' });
    segments.push({ text: state.title.trim(), step: "title" });
    segments.push({ text: '"' });
  }

  // Append the connecting word for the current step when its data is empty,
  // so "with ", "for ", etc. appear in the textarea as soon as the dialog opens.
  const step = state.step;
  if (step === "context" && !state.context) {
    segments.push({ text: " " });
  } else if (step === "venue" && !state.venue) {
    segments.push({ text: " at " });
  } else if (step === "serviceProvider" && !state.serviceProvider) {
    segments.push({ text: " at " });
  } else if (step === "partySize" && !state.partySize) {
    segments.push({ text: " for " });
  } else if (step === "contacts" && state.contacts.length === 0) {
    segments.push({ text: " with " });
  } else if (step === "time" && !state.timeDate) {
    // Use the picker's highlighted option to show/hide "on" in real-time
    const previewIsDayOfWeek =
      preview?.previewTime && DAYS_OF_WEEK.has(preview.previewTime.toLowerCase());
    segments.push({ text: previewIsDayOfWeek ? " on " : " " });
  } else if (step === "duration" && !state.duration) {
    segments.push({ text: " for " });
  } else if (step === "location" && !state.location) {
    // Use the picker's highlighted option to show/hide "at" in real-time
    const previewIsInPerson = preview?.previewLocation?.type === "in-person";
    segments.push({ text: previewIsInPerson ? " at " : ", " });
  } else if (step === "title" && !state.title) {
    segments.push({ text: ", titled " });
  }

  return segments;
}

/** Format a list of contacts as a natural-language string (e.g. "Jamie and Alex"). */
function formatContacts(contacts: ContactEntry[]): string {
  const formatted = contacts.map((c) => {
    const name = c.name.trim();
    const handle = (c.email || c.phone)?.trim();
    if (!name) return handle ?? "";
    return handle ? `${name} (${handle})` : name;
  });
  if (formatted.length === 1) {
    return formatted[0];
  }
  if (formatted.length === 2) {
    return `${formatted[0]} and ${formatted[1]}`;
  }
  const allButLast = formatted.slice(0, -1);
  return `${allButLast.join(", ")}, and ${formatted[formatted.length - 1]}`;
}

/** Derive the plain-text string from structured segments. */
function segmentsToText(segments: TextSegment[]): string {
  return segments.map((s) => s.text).join("");
}

/** Relative time phrases recognized in canned prompts. */
const PREFILL_TIME_PHRASES = ["next month", "next week", "this week", "tomorrow", "today"];

/**
 * Canned phrases that seed non-scheduling flows, keyed by the lowercased
 * whole phrase. Checked before trigger-word parsing so they cannot be
 * shadowed by a scheduling trigger, and matched as whole phrases only — a
 * bare "Add" or "Create" must never activate a flow.
 */
const GUIDED_PHRASE_SEEDS = new Map<string, { trigger: string; context: string }>([
  ["add a connector", { trigger: "Add", context: "a connector" }],
]);

/**
 * Parse a canned prompt string into a guided-flow seed.
 *
 * Non-scheduling phrase seeds (see {@link GUIDED_PHRASE_SEEDS}) take
 * precedence; everything else falls through to scheduling-prefill parsing.
 * Internal whitespace is collapsed so a pill label with a stray double space
 * still seeds its flow. Exported for tests.
 */
export function parseGuidedPrefill(
  text: string,
): { trigger: string; context?: string; time?: string } | null {
  const normalized = text.trim().toLowerCase().replace(/\s+/g, " ");
  const seed = GUIDED_PHRASE_SEEDS.get(normalized);
  if (seed) return seed;
  return parseSchedulingPrefill(text);
}

/**
 * The most words a canned scheduling prefill may carry after its trigger and
 * time phrase. The chips that seed the flow read "Schedule a meeting", "Grab
 * coffee", "Schedule a team sync": a short noun phrase. A longer prefill is a
 * whole request in its own words, and seeding the flow from it would rewrite
 * text the user chose.
 */
const PREFILL_CONTEXT_MAX_WORDS = 3;

/**
 * Parse a canned prompt string to extract scheduling details.
 *
 * Matches a trigger word at the start, then extracts:
 * - **context**: the noun phrase after the trigger (e.g. "a lunch")
 * - **time**: trailing relative time phrase (e.g. "next week")
 * - A trailing "with" is the prompt asking for the contact picker, and is dropped
 *
 * A prompt that already says what it is with ("Organize my inbox with labels as
 * emails arrive") is not asking the flow to fill one in, so it does not seed;
 * neither does one whose context runs past {@link PREFILL_CONTEXT_MAX_WORDS}.
 * Either way the text stays in the composer as written.
 *
 * @returns Parsed data or `null` if the text is not a canned scheduling prefill.
 */
function parseSchedulingPrefill(
  text: string,
): { trigger: string; context?: string; time?: string } | null {
  const trimmed = text.trim();
  if (!trimmed) return null;

  // Sort trigger words by length (longest first) for greedy matching.
  const sortedTriggers = [...TRIGGER_WORDS].sort((a, b) => b.length - a.length);

  for (const trigger of sortedTriggers) {
    if (!trimmed.toLowerCase().startsWith(trigger.toLowerCase())) continue;

    let rest = trimmed
      .slice(trigger.length)
      .trim()
      .replace(/\s+with$/i, "");
    if (!rest) return { trigger };
    if (/\swith\s/i.test(rest)) return null;

    // Extract trailing time phrase
    let time: string | undefined;
    const lowerRest = rest.toLowerCase();
    for (const phrase of PREFILL_TIME_PHRASES) {
      if (lowerRest.endsWith(` for ${phrase}`)) {
        time = phrase;
        rest = rest.slice(0, rest.length - ` for ${phrase}`.length).trim();
        break;
      }
      if (lowerRest.endsWith(` ${phrase}`)) {
        time = phrase;
        rest = rest.slice(0, rest.length - ` ${phrase}`.length).trim();
        break;
      }
    }

    if (rest.split(/\s+/).length > PREFILL_CONTEXT_MAX_WORDS) return null;
    const context = rest || undefined;
    return { trigger, context, time };
  }

  return null;
}
